/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Fragment, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { List } from 'react-virtualized/dist/commonjs';

import { Icon, Text, Separator } from '../../uiold';
import { hitSlop, themeColors } from '../../uiold/Constants';

import { useSimpleViewState } from './Context';
import PlacementActions from '../../../support/PlacementActions';
import getNewVendorPlacement from '../../../support/helpers/getNewVendorPlacement';
import formatDate from '../../../core/support/formatDate';

import { ListRowProps } from 'react-virtualized';

type DateItemProps = {
  date: string;
  disabled?: boolean;
  name?: string | null | undefined;
  onActionPress: (() => void) | null | undefined;
  onPress?: (() => void) | undefined;
  style: StyleSet;
};

function Header() {
  return (
    <Fragment>
      <View style={styles.header}>
        <View style={styles.date}>
          <Text style={styles.headerText}>Date</Text>
        </View>
        <View style={styles.vendor}>
          <Text style={styles.headerText}>Vendor</Text>
        </View>
        <View style={styles.action} />
      </View>
      <Separator style={styles.tableHeaderSeparator} />
    </Fragment>
  );
}

function DateItem(props: DateItemProps) {
  let { date, disabled, name, onActionPress, onPress, style } = props;

  return (
    <View style={style}>
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.6}
        disabled={disabled}
        style={styles.tableRow}
      >
        <View style={styles.date}>
          <Text style={disabled && styles.disabledTableText}>{date}</Text>
        </View>
        <View style={styles.vendor}>
          {name ? (
            <Text>{name}</Text>
          ) : (
            <Text style={styles.emptyRowText}>
              {disabled ? 'Not Applicable' : 'Available'}
            </Text>
          )}
        </View>
        <View style={styles.action}>
          {onActionPress && (
            <TouchableOpacity
              onPress={onActionPress}
              activeOpacity={0.6}
              hitSlop={hitSlop}
            >
              <Icon name="xCircle" size="M" color={themeColors.red} />
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
      <Separator opacity={0.4} style={styles.separator} />
    </View>
  );
}

function DateSelector() {
  let {
    activeIndex,
    activeVendor,
    market,
    placement,
    vendors,
  } = useSimpleViewState();

  let placementByDate = useMemo(
    () =>
      getNewVendorPlacement(market, (activeIndex ?? 0) + 1, {
        vendors,
        placement,
      }),
    [market, activeIndex, vendors, placement],
  );

  if (activeIndex == null || !activeVendor) {
    return null;
  }
  // Hacky; need non-nullable copy of these.
  // eslint-disable-next-line no-underscore-dangle
  let activeVendor_ = activeVendor;
  // eslint-disable-next-line no-underscore-dangle
  let activeIndex_ = activeIndex;

  let activeVendorDates = activeVendor.markets[market.id];

  let rowRenderer = ({ key, index, style }: ListRowProps) => {
    let date = String(market.dates[index]);
    // placedVendor might be undefined when the stall is empty at that date.
    let placedVendor = placementByDate[date];
    let onPress = () => {
      if (placedVendor) {
        if (placedVendor.id === activeVendor_.id) {
          // Do nothing if the active and the placed vendor is the same.
          return;
        }

        PlacementActions.removeVendorPlacement(
          market,
          date,
          activeIndex_,
          placedVendor,
        );
      }

      PlacementActions.addVendorPlacement(
        market,
        date,
        activeIndex_,
        activeVendor_,
      );
    };
    let onActionPress = () => {
      if (placedVendor) {
        // Right now we only have remove action.
        PlacementActions.removeVendorPlacement(
          market,
          date,
          activeIndex_,
          placedVendor,
        );
      }
    };
    // Disable the date item when the selected vendor doesn't sign up for that date.
    let disabled = !activeVendorDates?.includes(date);
    return (
      <DateItem
        key={key}
        date={formatDate(date, { fromUTC: true })}
        name={placedVendor && placedVendor.name}
        onPress={onPress}
        onActionPress={onActionPress}
        disabled={disabled}
        style={style as any}
      />
    );
  };

  return (
    <View style={styles.dateSelector}>
      <Header />
      <List
        width={375}
        height={ListHeight}
        rowCount={market.dates.length}
        rowHeight={DateItem.totalHeight}
        rowRenderer={rowRenderer}
      />
    </View>
  );
}

Header.height = 42;
Header.separatorHeight = Separator.size;
Header.totalHeight = Header.height + Header.separatorHeight;

DateItem.height = 60;
DateItem.separatorHeight = Separator.size;
DateItem.totalHeight = DateItem.height + DateItem.separatorHeight;

let ContainerHeight = 500;
let ListHeight = ContainerHeight - Header.totalHeight;

let styles = StyleSheet.create({
  dateSelector: {
    backgroundColor: themeColors.white,
    borderColor: themeColors.grey,
    borderWidth: StyleSheet.hairlineWidth,
    height: ContainerHeight,
  },
  header: {
    flexDirection: 'row',
    paddingVertical: 10,
    height: Header.height,
  },
  headerText: {
    fontWeight: '700',
  },
  tableHeaderSeparator: {
    backgroundColor: themeColors.grey,
    marginHorizontal: 14,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: DateItem.height,
  },
  disabledTableText: {
    color: themeColors.grey,
  },
  emptyRowText: {
    color: themeColors.grey,
  },
  date: {
    alignItems: 'center',
    width: 100,
  },
  vendor: {
    alignItems: 'center',
    width: 225,
  },
  action: {
    alignItems: 'center',
    minWidth: 50,
  },
  separator: {
    marginHorizontal: 14,
  },
});

export default DateSelector;
